/************************************************************************************************
 * Copyright TRUSST AI PTY LTD. All Rights Reserved.                                            *
 *                                                                                              *
 * Licensed under the TRUSST SOFTWARE LICENSE (the "License"). You may not use this file except *
 * in compliance with the "LICENSE" file accompanying this file. This file is distributed       *
 * on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, express or implied.       *
 *                                                                                              *
 * See the "License" file for the specific language governing permissions and limitations       *
 * under the License and limitations under the License.                                         *
 ***********************************************************************************************/

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import React, {useEffect, useState} from 'react';
import {ROWS_OPTIONS} from '../../hooks/useSuperTableOptions';

interface OptionsModalProps {
  open: boolean;
  currentRowsLimit: number;
  currentPreferModal: boolean;
  currentReapplyFilter: boolean;
  currentDoubleClickToOpen: boolean;
  currentReapplyGrouping: boolean;
  onClose: () => void;
  onSave: (
    rowsLimit: number,
    preferModal: boolean,
    reapplyFilter: boolean,
    reapplygrouping: boolean,
    doubleClickToOpen: boolean,
  ) => void;
}

export const SuperTableOptionsModal: React.FC<OptionsModalProps> = ({
  open,
  currentRowsLimit,
  currentPreferModal,
  currentReapplyFilter,
  currentDoubleClickToOpen,
  currentReapplyGrouping,
  onClose,
  onSave,
}) => {
  const [rowsLimit, setRowsLimit] = useState<number>(currentRowsLimit);
  const [preferModal, setPreferModal] = useState<boolean>(currentPreferModal);
  const [reapplyFilter, setReapplyFilter] = useState<boolean>(currentReapplyFilter);
  const [doubleClickToOpen, setDoubleClickToOpen] = useState<boolean>(currentDoubleClickToOpen);
  const [reapplygrouping, setReapplyGrouping] = useState<boolean>(currentReapplyGrouping);

  useEffect(() => {
    setRowsLimit(currentRowsLimit);
    setPreferModal(currentPreferModal);
    setReapplyFilter(currentReapplyFilter);
    setDoubleClickToOpen(currentDoubleClickToOpen);
    setReapplyGrouping(currentReapplyGrouping);
  }, [currentRowsLimit, currentPreferModal, currentReapplyFilter, currentDoubleClickToOpen, currentReapplyGrouping]);

  const handleSave = () => {
    onSave(rowsLimit, preferModal, reapplyFilter, reapplygrouping, doubleClickToOpen);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth={'sm'} fullWidth>
      <DialogTitle>Table Options</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={3}>
          <FormControl fullWidth>
            <InputLabel id={'rows-limit-label'}>Rows to Display</InputLabel>
            <Select
              labelId={'rows-limit-label'}
              value={rowsLimit}
              label={'Rows to Display'}
              onChange={(e) => setRowsLimit(Number(e.target.value))}
            >
              {ROWS_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {option.toLocaleString()}
                </MenuItem>
              ))}
            </Select>
            <Typography variant={'caption'} color={'text.secondary'}>
              Choose the maximum number of rows to load. (Large amounts may affect performance.)
            </Typography>
          </FormControl>
          <Stack direction={'row'} justifyContent={'space-between'} alignItems={'flex-start'} spacing={3}>
            <Box>
              <Typography variant={'subtitle2'}>Open Rows in Modal</Typography>
              <Typography variant={'caption'} color={'text.secondary'}>
                Clicking a row opens its details in a handy modal, keeping your place on the page.
              </Typography>
            </Box>
            <Switch checked={preferModal} onChange={(e) => setPreferModal(e.target.checked)} />
          </Stack>
          <Stack direction={'row'} justifyContent={'space-between'} alignItems={'flex-start'}>
            <Box>
              <Typography variant={'subtitle2'}>Remember Last Filter</Typography>
              <Typography variant={'caption'} color={'text.secondary'}>
                Automatically reload this table with your previous filter.
              </Typography>
            </Box>
            <Switch checked={reapplyFilter} onChange={(e) => setReapplyFilter(e.target.checked)} />
          </Stack>
          <Stack direction={'row'} justifyContent={'space-between'} alignItems={'flex-start'}>
            <Box>
              <Typography variant={'subtitle2'}>Remember Last Grouping</Typography>
              <Typography variant={'caption'} color={'text.secondary'}>
                Automatically reload the table with your previous grouping when refreshing the page.
              </Typography>
            </Box>
            <Switch checked={reapplygrouping} onChange={(e) => setReapplyGrouping(e.target.checked)} />
          </Stack>
          <Stack direction={'row'} justifyContent={'space-between'} alignItems={'flex-start'}>
            <Box>
              <Typography variant={'subtitle2'}>Double-Click to Open</Typography>
              <Typography variant={'caption'} color={'text.secondary'}>
                Require a double-click to view row details, making it easier to select and copy cell content.
              </Typography>
            </Box>
            <Switch checked={doubleClickToOpen} onChange={(e) => setDoubleClickToOpen(e.target.checked)} />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant={'contained'}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};
